import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Empty, Select, Space, Spin, Table, Tag, Tooltip, message } from "antd";
import styled from "styled-components";

import { presentationsApi } from "../../../api/presentationsApi";
import {
  DriveFolderStatus,
  IDriveFolderRow,
  IDriveFolders,
} from "../../../types/models/presentations";

/**
 * Which Drive folder each property's brochures go to.
 *
 * The folders under "SEND THESE — Current Brochures" are made by hand, so
 * discovery only *suggests* one per property and a person confirms it here.
 * Nothing is ever sent to a suggestion: a wrong guess would put one property's
 * pricing in another's folder. Confirming is what turns a property on.
 *
 * A property with no folder yet can have one made, named after it. That choice
 * is preselected when discovery found nothing, since it is then the likely
 * answer — but it still waits for the click, like every other decision here.
 */

/** The select's value for "make a new folder", which no Drive id can collide with. */
export const CREATE = "__create__";

const STATUS_LABELS: Record<DriveFolderStatus, string> = {
  suggested: "Suggested",
  not_found: "Not found",
  confirmed: "Confirmed",
  disabled: "Never send",
};

const STATUS_COLORS: Record<DriveFolderStatus, string> = {
  suggested: "gold",
  not_found: "red",
  confirmed: "green",
  disabled: "default",
};

/** Decisions still to make come first; decided ones sink. */
const ORDER: Record<DriveFolderStatus, number> = {
  suggested: 0, not_found: 1, confirmed: 2, disabled: 3,
};

const Body = styled.div`
  .toolbar {
    display: flex;
    gap: 12px;
    align-items: center;
    margin-bottom: 16px;
  }

  .sub {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }

  .unclaimed {
    margin-top: 24px;
  }
`;

const DriveFolders = () => {
  const [data, setData] = useState<IDriveFolders>();
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [saving, setSaving] = useState<number>();
  /** The folder picked in each row's select, before it is confirmed. */
  const [chosen, setChosen] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await presentationsApi.driveFolders());
    } catch (e) {
      message.error("Could not load Drive folders.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /** What the row's select shows: a person's pick, else the saved folder, else
   *  "create" when discovery found nothing. */
  const picked = (row: IDriveFolderRow) =>
    chosen[row.listing.id]
      ?? (row.folder_id || (row.status === "not_found" ? CREATE : undefined));

  const discover = async () => {
    setDiscovering(true);
    try {
      const counts = await presentationsApi.discoverDriveFolders();
      message.success(
        `${counts.suggested} suggested, ${counts.not_found} not found, `
        + `${counts.kept} already decided.`);
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail ?? "Could not look for folders.");
    } finally {
      setDiscovering(false);
    }
  };

  const decide = async (row: IDriveFolderRow, status: "confirmed" | "disabled") => {
    const folderId = picked(row);
    const folder = data?.folders.find((f) => f.id === folderId);
    setSaving(row.listing.id);
    try {
      if (status === "confirmed" && folderId === CREATE) {
        const saved = await presentationsApi.setDriveFolder(
          row.listing.id, { status, create: true });
        message.success(saved.created
          ? `Created the folder \u201c${saved.folder_name}\u201d for ${row.listing.name}.`
          : `A folder called \u201c${saved.folder_name}\u201d already existed, so ${row.listing.name} uses it.`);
      } else {
        await presentationsApi.setDriveFolder(row.listing.id, status === "confirmed"
          ? { status, folder_id: folderId, folder_name: folder?.name ?? row.folder_name }
          : { status });
        message.success(status === "confirmed"
          ? `${row.listing.name} now sends to ${folder?.name ?? row.folder_name}.`
          : `${row.listing.name} will not be sent to Drive.`);
      }
      setChosen(({ [row.listing.id]: _, ...rest }) => rest);
      load();
    } catch (e: any) {
      const body = e?.response?.data;
      message.error(body?.detail ?? body?.folder_id ?? "Could not save that.");
    } finally {
      setSaving(undefined);
    }
  };

  if (loading && !data) return <div style={{ padding: 60, textAlign: "center" }}><Spin /></div>;
  if (!data) return null;

  const rows = [...data.listings].sort((a, b) =>
    ORDER[a.status] - ORDER[b.status] || a.listing.name.localeCompare(b.listing.name));

  // Two listings pointing at one folder is normal for a development folder
  // (Maranta Garden and Maranta Ocean), and a mistake for two listings with the
  // same name — so it is pointed out, not prevented.
  const perFolder: Record<string, number> = {};
  rows.forEach((row) => {
    if (row.folder_id) perFolder[row.folder_id] = (perFolder[row.folder_id] ?? 0) + 1;
  });

  const folderOptions = (row: IDriveFolderRow) => {
    const options = data.folders.map((f) => ({ value: f.id, label: f.name }));
    // A folder made moments ago is saved on the row before Pathfinder has
    // indexed it; without this the select would show its bare id.
    if (row.folder_id && !data.folders.some((f) => f.id === row.folder_id)) {
      options.unshift({ value: row.folder_id, label: row.folder_name || row.folder_id });
    }
    return [
      { value: CREATE, label: `+ Create folder \u201c${row.listing.name}\u201d` },
      ...options,
    ];
  };

  const claimed = new Set(rows.filter((r) => r.folder_id).map((r) => r.folder_id));
  const unclaimed = data.folders.filter((folder) => !claimed.has(folder.id));

  const columns = [
    {
      title: "Property",
      key: "listing",
      render: (_: any, row: IDriveFolderRow) => (
        <>
          {row.listing.name}
          <div className="sub">#{row.listing.id}</div>
        </>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_: any, row: IDriveFolderRow) => (
        <>
          <Tag color={STATUS_COLORS[row.status]}>{STATUS_LABELS[row.status]}</Tag>
          {row.match && (
            <div className="sub">
              {row.match === "exact" ? "Same name" : "Folder name starts the property's"}
            </div>
          )}
        </>
      ),
    },
    {
      title: "Folder",
      key: "folder",
      width: 280,
      render: (_: any, row: IDriveFolderRow) => (
        <>
          <Select
            aria-label={`folder for ${row.listing.id}`}
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="label"
            placeholder="Choose a folder"
            value={picked(row)}
            onChange={(value: string) =>
              setChosen((current) => ({ ...current, [row.listing.id]: value }))}
            options={folderOptions(row)}
          />
          {row.folder_id && perFolder[row.folder_id] > 1 && (
            <div className="sub">
              Shared with {perFolder[row.folder_id] - 1} other propert
              {perFolder[row.folder_id] === 2 ? "y" : "ies"}
            </div>
          )}
        </>
      ),
    },
    {
      title: "",
      key: "actions",
      render: (_: any, row: IDriveFolderRow) => {
        const choice = picked(row);
        const unchanged = row.status === "confirmed" && choice === row.folder_id;
        const creating = choice === CREATE;
        return (
          <Space>
            <Tooltip
              title={!choice ? "Choose a folder first"
                : unchanged ? "Already confirmed"
                  : creating ? "Makes the folder in \u201cSEND THESE\u201d and sends there from now on"
                    : ""}
            >
              <Button
                type="primary"
                size="small"
                disabled={!choice || unchanged}
                loading={saving === row.listing.id}
                onClick={() => decide(row, "confirmed")}
              >
                {creating ? "Create & confirm" : "Confirm"}
              </Button>
            </Tooltip>
            {row.status !== "disabled" && (
              <Button size="small" onClick={() => decide(row, "disabled")}>
                Never send
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Body>
      {!data.sync_enabled && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Drive sync is switched off here"
          description="Folders can be confirmed, but nothing is sent until PRESENTATIONS_DRIVE_SYNC is on."
        />
      )}
      {!data.configured && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="No Drive service account is configured"
        />
      )}
      {data.folders_error && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Could not list the Drive folders"
          description={`${data.folders_error} — the choices below are empty until Pathfinder answers.`}
        />
      )}

      <div className="toolbar">
        <Button onClick={discover} loading={discovering}>Look for folders</Button>
        <span className="sub">
          Suggests a folder for every property with a presentation. Never changes
          one that has been confirmed or turned off.
        </span>
      </div>

      {rows.length === 0 ? (
        <Empty description="No property has a folder yet — press Look for folders." />
      ) : (
        <Table
          rowKey={(row) => row.listing.id}
          size="small"
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 50, hideOnSinglePage: true }}
        />
      )}

      {unclaimed.length > 0 && (
        <div className="unclaimed">
          <div className="sub" style={{ marginBottom: 8 }}>
            Folders no property points at yet
          </div>
          <Space wrap>
            {unclaimed.map((folder) => <Tag key={folder.id}>{folder.name}</Tag>)}
          </Space>
        </div>
      )}
    </Body>
  );
};

export default DriveFolders;
